import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';
import { ArticleSchema } from '@/components/feature/SeoSchema';

export default function BasetaoSafetyGuide() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <main className="min-h-screen">
      <Header />
      <ArticleSchema slug="basetao-safety-guide" />
      <article className="pt-24 pb-16 md:pb-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <nav className="mb-8" aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 text-sm text-foreground-500">
              <li><Link to="/" className="hover:text-foreground-900 transition-colors whitespace-nowrap">Home</Link></li>
              <li><i className="ri-arrow-right-s-line text-xs"></i></li>
              <li className="text-foreground-900 font-medium">Safety & Security</li>
            </ol>
          </nav>

          <header className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 text-xs font-semibold tracking-wider uppercase text-primary-700 bg-primary-100 rounded-full">Safety</span>
              <span className="text-xs text-foreground-500">Updated August 2026</span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-900 leading-tight mb-5">
              Basetao Safety & Security Guide 2026 — Protect Your Shopping
            </h1>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
              Shopping safely on <strong>Basetao</strong> involves understanding the platform&apos;s security measures and following best practices for account protection, payment safety, and scam awareness. This guide covers everything you need to know to shop with confidence.
            </p>
          </header>

          <div className="prose-custom space-y-8">
            <section>
              <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-4">Essential Safety Practices</h2>
              <ul className="space-y-3 ml-5">
                <li className="text-base text-foreground-600 leading-relaxed"><strong className="text-foreground-900">Use a strong unique password:</strong> Create a password specifically for your Basetao account that you do not reuse elsewhere.</li>
                <li className="text-base text-foreground-600 leading-relaxed"><strong className="text-foreground-900">Enable two-factor authentication:</strong> If Basetao offers 2FA, enable it immediately for an additional layer of account protection.</li>
                <li className="text-base text-foreground-600 leading-relaxed"><strong className="text-foreground-900">Verify the website URL:</strong> Always access Basetao through the official domain. Phishing sites with similar URLs exist and can steal your credentials.</li>
                <li className="text-base text-foreground-600 leading-relaxed"><strong className="text-foreground-900">Review payment security:</strong> Use payment methods with buyer protection such as PayPal when available. Avoid direct bank transfers to unknown accounts.</li>
                <li className="text-base text-foreground-600 leading-relaxed"><strong className="text-foreground-900">Monitor your orders:</strong> Regularly check your order status and QC photos. Promptly report any discrepancies or suspicious activity.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-4">Recognizing and Avoiding Scams</h2>
              <p className="text-base text-foreground-600 leading-relaxed mb-4">
                While Basetao itself is a legitimate platform, the broader ecosystem of international shopping includes bad actors. Common scams to watch for include fake Basetao websites with similar-looking URLs, social media accounts impersonating Basetao support, and unsolicited messages offering exclusive deals or discounts. Always initiate contact through official Basetao channels and verify any promotional offers directly on the platform.
              </p>
            </section>

            <section className="p-6 rounded-xl bg-background-100 border border-background-200/70">
              <h2 className="text-xl font-heading font-bold text-foreground-900 mb-5">FAQ</h2>
              <div className="space-y-5">
                {[
                  { q: 'Is my payment information safe on Basetao?', a: 'Basetao uses standard payment processing with encryption. Using PayPal adds an extra layer of buyer protection since Basetao never sees your card details.' },
                  { q: 'What should I do if I suspect a phishing attempt?', a: 'Do not click any links or provide information. Report the incident to Basetao support through the official platform and change your account password immediately.' },
                ].map((faq, idx) => (
                  <div key={idx}><h3 className="text-base font-semibold text-foreground-900 mb-1.5">{faq.q}</h3><p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p></div>
                ))}
              </div>
            </section>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Link to="/" className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-full transition-colors duration-300 whitespace-nowrap"><i className="ri-home-4-line"></i>Back to Homepage</Link>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold text-foreground-900 bg-background-100 hover:bg-background-200 border border-background-300/60 rounded-full transition-colors duration-300 whitespace-nowrap">Visit Main Website<i className="ri-external-link-line"></i></a>
            </div>
          </div>
        </div>
      </article>
      <Footer />
    </main>
  );
}